import { formatDate } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Plus, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { StepUpCode } from '../../components/admin/step-up.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import {
  keys,
  reauthenticate,
  useApiMutation,
  useAssignableRoles,
  useInvitations,
  useMembers,
  useTeams,
  type Invitation,
  type Member,
} from '../../lib/admin-api.ts';
import { api, errorMessage } from '../../lib/api.ts';
import { reloadAt } from '../../lib/navigation.ts';
import { STATUS_LABELS } from '../../lib/permission-texts.ts';
import { can, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

const STATUS_TONE = { active: 'success', suspended: 'warning', ended: 'neutral' } as const;

export function UsersPage() {
  const { data: session } = useSession();
  const [includeFormer, setIncludeFormer] = useState(false);
  const [search, setSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  const [selected, setSelected] = useState<Member | null>(null);
  const members = useMembers(includeFormer);
  const timeZone = session?.tenant.timezone;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (members.data ?? []).filter(
      (m) =>
        !term ||
        m.displayName.toLowerCase().includes(term) ||
        (m.email ?? '').toLowerCase().includes(term),
    );
  }, [members.data, search]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Usuarios</h1>
          <p className="text-sm text-muted-foreground">
            Personas de tu inmobiliaria, sus roles y sus invitaciones.
          </p>
        </div>
        <Button
          variant="brand"
          onClick={() => {
            setInviting(true);
          }}
        >
          <UserPlus /> Invitar
        </Button>
      </div>

      <section aria-labelledby="miembros" className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 id="miembros" className="text-lg font-semibold">
            Miembros
          </h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              type="search"
              aria-label="Buscar por nombre o correo"
              placeholder="Buscar por nombre o correo"
              className="sm:w-64"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-5"
                checked={includeFormer}
                onChange={(event) => {
                  setIncludeFormer(event.target.checked);
                }}
              />
              Ver exmiembros
            </label>
          </div>
        </div>
        {members.isPending ? (
          <Loading />
        ) : members.error ? (
          <Alert tone="danger">{errorMessage(members.error)}</Alert>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay miembros que coincidan.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
            {visible.map((m) => (
              <li key={m.id} className="flex items-center gap-3 p-3 sm:p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {m.displayName}
                    {m.userId === session?.user.id && (
                      <span className="text-muted-foreground"> (tú)</span>
                    )}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">
                    {m.email ?? 'Ya no pertenece a la inmobiliaria'}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge tone={STATUS_TONE[m.status]}>{STATUS_LABELS[m.status]}</Badge>
                    <span>{m.role.name}</span>
                    {m.team && <span>· {m.team.name}</span>}
                    <span className="hidden sm:inline">
                      · Desde {formatDate(m.createdAt, timeZone)}
                    </span>
                  </div>
                </div>
                {m.status !== 'ended' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Acciones para ${m.displayName}`}
                    onClick={() => {
                      setSelected(m);
                    }}
                  >
                    <MoreHorizontal />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <InvitationsSection />
      <TeamsSection />

      <InviteDialog
        open={inviting}
        onClose={() => {
          setInviting(false);
        }}
      />
      {selected && (
        <MemberDialog
          member={selected}
          isSelf={selected.userId === session?.user.id}
          canImpersonate={can(session, 'users.impersonate')}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const roles = useAssignableRoles();
  const teams = useTeams();
  const notify = useToast();
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [teamId, setTeamId] = useState('');
  const invite = useApiMutation(
    (body: { email: string; roleId: string; teamId: string | null }) =>
      api<Invitation>('/invitations', { method: 'POST', body }),
    [keys.invitations],
  );

  const submit = () => {
    invite.mutate(
      { email, roleId, teamId: teamId || null },
      {
        onSuccess: (result) => {
          notify(
            result.emailSent === false
              ? 'La invitación se creó pero el correo no salió. Usa "Reenviar".'
              : `Enviamos la invitación a ${result.email}.`,
            result.emailSent === false ? 'error' : 'success',
          );
          setEmail('');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invitar a una persona"
      description="Le enviaremos un enlace para crear su cuenta. Vence en 72 horas."
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {invite.error && <Alert tone="danger">{errorMessage(invite.error)}</Alert>}
        <Field label="Correo electrónico">
          {(a11y) => (
            <Input
              {...a11y}
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Rol">
          {(a11y) => (
            <Select
              {...a11y}
              required
              value={roleId}
              onChange={(event) => {
                setRoleId(event.target.value);
              }}
            >
              <option value="">Elige un rol</option>
              {roles.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Equipo (opcional)">
          {(a11y) => (
            <Select
              {...a11y}
              value={teamId}
              onChange={(event) => {
                setTeamId(event.target.value);
              }}
            >
              <option value="">Sin equipo</option>
              {teams.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button
          type="submit"
          className="w-full"
          loading={invite.isPending}
          disabled={!email || !roleId}
        >
          Enviar invitación
        </Button>
      </form>
    </Dialog>
  );
}

function MemberDialog({
  member,
  isSelf,
  canImpersonate,
  onClose,
}: {
  member: Member;
  isSelf: boolean;
  canImpersonate: boolean;
  onClose: () => void;
}) {
  const roles = useAssignableRoles();
  const teams = useTeams();
  const notify = useToast();
  const [roleId, setRoleId] = useState(member.role.id);
  const [teamId, setTeamId] = useState(member.team?.id ?? '');
  const [confirming, setConfirming] = useState<'suspend' | 'end' | 'mfa' | 'impersonate' | null>(
    null,
  );
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const memberKeys = [keys.members(false), keys.members(true), keys.teams, keys.roles];

  const update = useApiMutation(
    (body: Record<string, unknown>) => api(`/members/${member.id}`, { method: 'PATCH', body }),
    memberKeys,
  );

  const run = async (action: () => Promise<unknown>, done: string, close = true) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      notify(done, 'success');
      if (close) onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={member.displayName}
      description={member.email ?? undefined}
    >
      <div className="space-y-6">
        {error && <Alert tone="danger">{error}</Alert>}

        <section className="space-y-3">
          <h3 className="font-semibold">Rol y equipo</h3>
          <Field label="Rol">
            {(a11y) => (
              <Select
                {...a11y}
                value={roleId}
                onChange={(event) => {
                  setRoleId(event.target.value);
                }}
              >
                {roles.data?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Equipo">
            {(a11y) => (
              <Select
                {...a11y}
                value={teamId}
                onChange={(event) => {
                  setTeamId(event.target.value);
                }}
              >
                <option value="">Sin equipo</option>
                {teams.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            loading={update.isPending}
            disabled={roleId === member.role.id && teamId === (member.team?.id ?? '')}
            onClick={() =>
              void run(
                () => update.mutateAsync({ roleId, teamId: teamId || null }),
                'Cambios guardados.',
              )
            }
          >
            Guardar cambios
          </Button>
        </section>

        {!isSelf && (
          <section className="space-y-3">
            <h3 className="font-semibold">Estado</h3>
            {member.status === 'suspended' ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void run(() => update.mutateAsync({ status: 'active' }), 'Miembro reactivado.')
                }
              >
                Reactivar
              </Button>
            ) : confirming === 'suspend' ? (
              <DeactivateBox
                member={member}
                text="Al suspenderlo se cerrarán todas sus sesiones de inmediato. Podrás reactivarlo después."
                action="Suspender"
                busy={busy}
                onConfirm={(reassignTo) =>
                  void run(
                    () =>
                      update.mutateAsync({
                        status: 'suspended',
                        ...(reassignTo ? { reassignTo } : {}),
                      }),
                    'Miembro suspendido.',
                  )
                }
                onCancel={() => {
                  setConfirming(null);
                }}
              />
            ) : confirming === 'end' ? (
              <DeactivateBox
                member={member}
                text="Terminar la membresía es definitivo: la persona deja la inmobiliaria y se cierran sus sesiones. Su nombre se conserva en el historial."
                action="Terminar membresía"
                busy={busy}
                onConfirm={(reassignTo) =>
                  void run(
                    () =>
                      update.mutateAsync({
                        status: 'ended',
                        ...(reassignTo ? { reassignTo } : {}),
                      }),
                    'Membresía terminada.',
                  )
                }
                onCancel={() => {
                  setConfirming(null);
                }}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setConfirming('suspend');
                  }}
                >
                  Suspender
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setConfirming('end');
                  }}
                >
                  Terminar membresía
                </Button>
              </div>
            )}
          </section>
        )}

        {!isSelf && member.status === 'active' && (
          <section className="space-y-3">
            <h3 className="font-semibold">Acceso</h3>
            <Button
              variant="secondary"
              onClick={() =>
                void run(
                  () => api(`/members/${member.id}/password-reset`, { method: 'POST' }),
                  'Le enviamos un enlace para elegir una contraseña nueva.',
                  false,
                )
              }
            >
              Enviar enlace para cambiar la contraseña
            </Button>
            {confirming === 'mfa' ? (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <p className="text-sm">
                  Se invalidarán su aplicación de autenticación y sus códigos de respaldo, se
                  cerrarán sus sesiones y tendrá que activar la verificación de nuevo. Le avisaremos
                  por correo.
                </p>
                <StepUpCode onChange={setCode} />
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    loading={busy}
                    disabled={code.length !== 6}
                    onClick={() =>
                      void run(async () => {
                        await reauthenticate(code);
                        await api(`/members/${member.id}/2fa-reset`, { method: 'POST' });
                      }, 'Verificación en dos pasos reiniciada.')
                    }
                  >
                    Reiniciar verificación
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setConfirming(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirming('mfa');
                  setCode('');
                }}
              >
                Reiniciar verificación en dos pasos
              </Button>
            )}
            {canImpersonate &&
              (confirming === 'impersonate' ? (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <p className="text-sm">
                    Verás la aplicación como {member.displayName} durante máximo 60 minutos. Todo lo
                    que hagas queda registrado a nombre de ambos y le avisaremos por correo con el
                    motivo.
                  </p>
                  <Field
                    label="Motivo"
                    hint="Al menos 15 caracteres. Por ejemplo: revisar por qué no ve sus contactos."
                  >
                    {(a11y) => (
                      <textarea
                        {...a11y}
                        className="min-h-20 w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm"
                        value={reason}
                        onChange={(event) => {
                          setReason(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <StepUpCode onChange={setCode} />
                  <Button
                    loading={busy}
                    disabled={code.length !== 6 || Array.from(reason.trim()).length < 15}
                    onClick={() =>
                      void run(async () => {
                        await reauthenticate(code);
                        await api('/impersonation', {
                          method: 'POST',
                          body: { membershipId: member.id, reason: reason.trim() },
                        });
                        // A different session now: reload the whole app with it.
                        reloadAt('/');
                      }, 'Accediendo…')
                    }
                  >
                    Acceder como {member.displayName}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setConfirming('impersonate');
                    setCode('');
                  }}
                >
                  Acceder como esta persona
                </Button>
              ))}
          </section>
        )}
      </div>
    </Dialog>
  );
}

interface Portfolio {
  contacts: number;
  deals: number;
  properties: number;
  total: number;
}

const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

/**
 * HU-CON-17: deactivating someone with contacts, deals or properties asks who gets
 * all of them (one person); they move in the same operation.
 */
function DeactivateBox({
  member,
  text,
  action,
  busy,
  onConfirm,
  onCancel,
}: {
  member: Member;
  text: string;
  action: string;
  busy: boolean;
  onConfirm: (reassignTo: string | null) => void;
  onCancel: () => void;
}) {
  const portfolio = useQuery({
    queryKey: ['members', member.id, 'portfolio'],
    queryFn: () => api<Portfolio>(`/members/${member.id}/portfolio`),
  });
  const members = useMembers(false);
  const [receiver, setReceiver] = useState('');
  const p = portfolio.data;
  const needsReceiver = (p?.total ?? 0) > 0;
  const summary = p
    ? [
        p.contacts ? plural(p.contacts, 'contacto', 'contactos') : null,
        p.deals ? plural(p.deals, 'negocio', 'negocios') : null,
        p.properties ? plural(p.properties, 'propiedad', 'propiedades') : null,
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  return (
    <div
      className="space-y-3 rounded-lg border border-border p-3"
      role="group"
      aria-label={`Confirmar: ${action}`}
    >
      <p className="text-sm">{text}</p>
      {portfolio.isPending ? (
        <p className="text-sm text-muted-foreground">Revisando su cartera…</p>
      ) : needsReceiver ? (
        <Field
          label="¿Quién recibe su cartera?"
          hint={`A su cargo: ${summary}. Todo pasa a la persona que elijas.`}
        >
          {(a11y) => (
            <Select
              {...a11y}
              value={receiver}
              onChange={(event) => {
                setReceiver(event.target.value);
              }}
            >
              <option value="">Elige a alguien</option>
              {members.data
                ?.filter((m) => m.status === 'active' && m.id !== member.id)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      ) : null}
      <div className="flex gap-2">
        <Button
          variant="danger"
          loading={busy}
          disabled={portfolio.isPending || (needsReceiver && !receiver)}
          onClick={() => {
            onConfirm(needsReceiver ? receiver : null);
          }}
        >
          {action}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function InvitationsSection() {
  const { data: session } = useSession();
  const invitations = useInvitations();
  const roles = useAssignableRoles();
  const notify = useToast();
  const refresh = [keys.invitations];
  const resend = useApiMutation(
    (id: string) => api<Invitation>(`/invitations/${id}/resend`, { method: 'POST' }),
    refresh,
  );
  const revoke = useApiMutation(
    (id: string) => api(`/invitations/${id}`, { method: 'DELETE' }),
    refresh,
  );
  const roleName = (id: string) => roles.data?.find((r) => r.id === id)?.name ?? 'Rol';

  return (
    <section aria-labelledby="invitaciones" className="space-y-3">
      <h2 id="invitaciones" className="text-lg font-semibold">
        Invitaciones pendientes
      </h2>
      {invitations.isPending ? (
        <Loading />
      ) : invitations.error ? (
        <Alert tone="danger">{errorMessage(invitations.error)}</Alert>
      ) : invitations.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay invitaciones pendientes.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {invitations.data.map((inv) => (
            <li key={inv.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{inv.email}</p>
                <p className="text-sm text-muted-foreground">
                  {roleName(inv.roleId)} · Vence el{' '}
                  {formatDate(inv.expiresAt, session?.tenant.timezone)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={resend.isPending && resend.variables === inv.id}
                  onClick={() => {
                    resend.mutate(inv.id, {
                      onSuccess: () => {
                        notify(`Reenviamos la invitación a ${inv.email}.`, 'success');
                      },
                      onError: (err) => {
                        notify(errorMessage(err), 'error');
                      },
                    });
                  }}
                >
                  Reenviar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    revoke.mutate(inv.id, {
                      onSuccess: () => {
                        notify('Invitación revocada.', 'success');
                      },
                      onError: (err) => {
                        notify(errorMessage(err), 'error');
                      },
                    });
                  }}
                >
                  Revocar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TeamsSection() {
  const teams = useTeams();
  const notify = useToast();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const create = useApiMutation(
    (body: { name: string }) => api('/teams', { method: 'POST', body }),
    [keys.teams],
  );
  const rename = useApiMutation(
    (input: { id: string; name: string }) =>
      api(`/teams/${input.id}`, { method: 'PATCH', body: { name: input.name } }),
    [keys.teams, keys.members(false)],
  );

  return (
    <section aria-labelledby="equipos" className="space-y-3">
      <h2 id="equipos" className="text-lg font-semibold">
        Equipos
      </h2>
      <Card className="space-y-4">
        {teams.data?.length === 0 && (
          <div className="space-y-1 text-sm">
            <p className="font-medium">Todavía no hay equipos.</p>
            <p className="text-muted-foreground">
              Un equipo agrupa a varios agentes bajo un líder. Quien tenga un rol con alcance
              «Equipo» ve y trabaja los registros de su equipo, no los de toda la inmobiliaria. Por
              ejemplo: «Ventas zona norte» o «Alquileres».
            </p>
          </div>
        )}
        <ul className="flex flex-wrap gap-2 empty:hidden">
          {teams.data?.map((t) => (
            <li key={t.id}>
              <Button
                size="sm"
                variant="secondary"
                aria-label={`Renombrar equipo ${t.name}`}
                onClick={() => {
                  setEditing({ id: t.id, name: t.name });
                }}
              >
                {t.name} ({t.memberCount}) <Pencil />
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(
              { name: name.trim() },
              {
                onSuccess: () => {
                  setName('');
                  notify('Equipo creado.', 'success');
                },
                onError: (err) => {
                  notify(errorMessage(err), 'error');
                },
              },
            );
          }}
        >
          <Input
            aria-label="Nombre del equipo nuevo"
            placeholder="Nombre del equipo nuevo"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <Button
            type="submit"
            variant="secondary"
            loading={create.isPending}
            disabled={!name.trim()}
          >
            <Plus /> Crear
          </Button>
        </form>
      </Card>
      {editing && (
        <Dialog
          open
          title="Renombrar equipo"
          onClose={() => {
            setEditing(null);
          }}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate(editing, {
                onSuccess: () => {
                  setEditing(null);
                  notify('Equipo renombrado.', 'success');
                },
              });
            }}
          >
            {rename.error && <Alert tone="danger">{errorMessage(rename.error)}</Alert>}
            <Field label="Nombre">
              {(a11y) => (
                <Input
                  {...a11y}
                  value={editing.name}
                  onChange={(event) => {
                    setEditing({ ...editing, name: event.target.value });
                  }}
                />
              )}
            </Field>
            <Button
              type="submit"
              className="w-full"
              loading={rename.isPending}
              disabled={!editing.name.trim()}
            >
              Guardar
            </Button>
          </form>
        </Dialog>
      )}
    </section>
  );
}
